import {
  BaseColor,
  Function,
  PaletteColor,
  Radius,
  Spacing,
  StackIndex,
  ThemeColor,
  Tint,
  Transition,
  ZIndex,
} from '@thiememeulenhoff/tokens';
import { css, CSSResult, unsafeCSS } from 'lit';

// TODO: move these types to token library
export type ThemeTypographyNamespace =
  'label' | 'nav' | 'body' | 'heading' | 'display';
export type ThemeTypographySize = 'small' | 'medium' | 'large';
export type ThemeTypographyProperty =
  'size' | 'weight' | 'line-height' | 'tablet-size' | 'font-family';

export type ThemeTypographyVariantsByNamespace = {
  display: 'sans-serif' | 'serif';
  heading: 'sans-serif' | 'serif';
  label: 'regular' | 'semi-bold' | 'light';
  body: 'regular' | 'bold' | 'light';
  nav: 'regular' | 'semi-bold';
};

const TYPOGRAPHY_VARIANTS_BY_NAMESPACE: {
  [N in ThemeTypographyNamespace]: Array<ThemeTypographyVariantsByNamespace[N]>;
} = {
  display: ['sans-serif', 'serif'],
  heading: ['sans-serif', 'serif'],
  label: ['regular', 'semi-bold', 'light'],
  body: ['regular', 'bold', 'light'],
  nav: ['regular', 'semi-bold'],
};

export type BorderStyle = 'solid' | 'dashed' | 'dotted';

export const BASE_FONT_SIZE = 16;

/**
 * Transform given pixel value(s) to rem value(s)
 *
 * @param px Array of numbers representing pixel values
 * @returns string of rem values
 */
export const rem = (...px: Array<number>) =>
  unsafeCSS(
    px.reduce((acc, value) => `${acc} ${value / BASE_FONT_SIZE}rem`, '').trim(),
  );

/**
 * Get a prefixed token by name
 *
 * @param name token name without any prefixes
 * @returns a valid prefixed token, i.e. `--tm-base-font-family`
 */
export const tokenName = (name: string) => {
  return unsafeCSS(`--tm-${name}`);
};

/**
 * Get a css var declaration with a prefixed token by name
 *
 * @param name token name without any prefixes
 * @returns a valid prefixed token, i.e. `--tm-base-font-family`
 */
export const token = (name: string) => {
  return css`var(${tokenName(name)})`;
};

/**
 * Get a typography token
 *
 * @param namespace `ThemeTypographyNamespace`
 * @param size `ThemeTypographySize`
 * @param variant variant allowed for the given namespace (see `ThemeTypographyVariantsByNamespace`)
 * @param property `ThemeTypographyProperty`
 * @returns a typography token, i.e. `--tm-typography-body-small-regular-line-height`
 */
export const typography = <N extends ThemeTypographyNamespace>(
  namespace: N,
  size: ThemeTypographySize,
  variant: ThemeTypographyVariantsByNamespace[N],
  property: ThemeTypographyProperty,
) => {
  const allowedVariants = TYPOGRAPHY_VARIANTS_BY_NAMESPACE[namespace];
  if (!allowedVariants.includes(variant)) {
    throw new Error(
      `Variant "${String(variant)}" is not allowed for namespace "${namespace}". Allowed variants: ${allowedVariants.join(', ')}.`,
    );
  }

  const value = `var(${tokenName(`typography-${namespace}-${size}-${String(variant)}-${property}`)})`;

  // Token values are unitless numbers; line-height must always resolve to a px value.
  return unsafeCSS(value);
};

/**
 * Get a css color value for a theme color
 *
 * @param colorName `ThemeColor | PaletteColor | 'black' | 'white`
 * @param colorTint `ThemeColorTint`
 * @returns a var declaration for a color token, i.e. `var(--tm-color-alpha-500)`
 */
export const color = (
  colorName: ThemeColor | PaletteColor | BaseColor,
  colorTint: Tint = 500,
) => {
  if (['white', 'black'].includes(colorName)) {
    return unsafeCSS(`var(${tokenName(`color-${colorName}`)})`);
  }

  const customProperty = tokenName(`color-${colorName}-${colorTint}`);
  const hslColor = `var(${customProperty})`;
  return unsafeCSS(hslColor);
};

/**
 * Get a css color value for tint
 *
 * @param tint `ThemeColorTint`
 * @returns an hsl color for a tint, i.e. `hsl(var(--tm-color-tint-500))`
 */
export const tint = (tint: Tint) => {
  return color('neutral', tint);
};

/**
 * Make the given color transparent.
 *
 * @param color `CSSResult` The color to make transparent
 * @param transparency `number` The transparency value
 * @returns an hsl color with transparency, i.e. `hsl(from var(--tm-color-primary-500) h s l / 50%)`
 */
export const transparent = (color: CSSResult, transparency: number) => {
  return unsafeCSS(css`hsl(from ${color} h s l / ${transparency}%)`);
};

/**
 * Get the full value for a border
 *
 * @param size `number` representing pixel value
 * @param colorName `ThemeColor | 'black' | 'white`
 * @param colorTint `Tint`
 * @param borderStyle `BorderStyle`
 * @returns valid border value, i.e. `1px solid hsl(var(--tm-color-primary-500))`
 */
export const border = (
  size: number,
  colorName: ThemeColor | PaletteColor | BaseColor = 'neutral',
  colorTint: Tint = 500,
  borderStyle: BorderStyle = 'solid',
) => css`
  ${rem(size)} ${unsafeCSS(borderStyle)} ${color(colorName, colorTint)}
`;

/**
 * Get the full value for a border, using a semantic color token
 *
 * @param size `number` representing pixel value
 * @param colorToken `string` representing the semantic color token
 * @param borderStyle `BorderStyle`
 * @returns valid border value, i.e. `1px solid var(--tm-border-subtle)`
 */
export const border2 = (
  size: number,
  colorToken: string | CSSResult,
  borderStyle: BorderStyle = 'solid',
) => css`
  ${rem(size)} ${unsafeCSS(borderStyle)} ${typeof colorToken === 'string' ? token(colorToken) : colorToken}
`;

/**
 * Set vertical spacing using bottom margin on all but the last child
 *
 * @param spacing `number` representing pixel value
 * @returns css to set bottom margin on all but the last child
 */
export const verticalSpacing = (spacing = 16) => css`
  :host(:not(:last-child)) {
    margin-block-end: ${rem(spacing)};
  }
`;

/**
 * Get typography styles for a given namespace, size and variant
 *
 * @param namespace `ThemeTypographyNamespace`
 * @param size `ThemeTypographySize`
 * @param variant variant allowed for the given namespace (see `ThemeTypographyVariantsByNamespace`)
 * @returns full css set for setting text styles for a given namespace, size and variant
 */
export const text = <N extends ThemeTypographyNamespace>(
  namespace: N,
  size: ThemeTypographySize = 'medium',
  variant?: ThemeTypographyVariantsByNamespace[N],
) => {
  const resolvedVariant =
    variant ??
    (TYPOGRAPHY_VARIANTS_BY_NAMESPACE[
      namespace
    ][0] as ThemeTypographyVariantsByNamespace[N]);

  const baseCSS = css`
    font-family: ${typography(namespace, size, resolvedVariant, 'font-family')};
    font-size: ${typography(namespace, size, resolvedVariant, 'size')};
    font-weight: ${typography(namespace, size, resolvedVariant, 'weight')};
    line-height: ${typography(namespace, size, resolvedVariant, 'line-height')};
  `;

  let mediaCSS = css``;
  if (namespace === 'display') {
    mediaCSS = css`
      @media screen and (max-width: 1024px) {
        font-size: ${typography(namespace, size, resolvedVariant, 'tablet-size')};
      }
    `;
  }

  return css`
    ${baseCSS}
    ${mediaCSS}
  `;
};

export const display = (
  size: ThemeTypographySize = 'medium',
  variant: ThemeTypographyVariantsByNamespace['display'] = 'sans-serif',
) => {
  return text('display', size, variant);
};

export const heading = (
  size: ThemeTypographySize = 'medium',
  variant: ThemeTypographyVariantsByNamespace['heading'] = 'sans-serif',
) => {
  return text('heading', size, variant);
};

export const nav = (
  size: ThemeTypographySize = 'medium',
  variant: ThemeTypographyVariantsByNamespace['nav'] = 'regular',
) => {
  return text('nav', size, variant);
};

export const body = (
  size: ThemeTypographySize = 'medium',
  variant: ThemeTypographyVariantsByNamespace['body'] = 'regular',
) => {
  return text('body', size, variant);
};

export const label = (
  size: ThemeTypographySize = 'small',
  variant: ThemeTypographyVariantsByNamespace['label'] = 'regular',
) => {
  return text('label', size, variant);
};

/**
 * Get css to apply styling on only not-last-child items of those selectors
 *
 * @param selectors list of selectors
 * @param styles `CSSResult` to apply
 * @returns full css result to apply styles to not-last-children
 */
export const whereNotLastChild = (
  selectors: Array<string>,
  styles: CSSResult,
) => {
  const whereSelectors = selectors
    .map((selector) => {
      return `${selector}:not(:last-child)`;
    })
    .join(',');

  return css`
    :where(${unsafeCSS(whereSelectors)}) {
      ${styles}
    }
  `;
};

export const spacing = (...spacing: Array<Spacing>) => {
  return unsafeCSS(
    spacing
      .reduce(
        (acc, value) => `${acc} var(${tokenName(`spacing-space-${value}`)})`,
        '',
      )
      .trim(),
  );
};

export const transition = (
  property: string,
  duration: Transition,
  transition: Function,
) => {
  return unsafeCSS(
    `${property} var(${tokenName(`transition-duration-${duration}`)}) var(${tokenName(
      `transition-function-${transition}`,
    )})`,
  );
};

export const radius = (...radius: Array<Radius>) => {
  return unsafeCSS(
    radius
      .reduce((acc, value) => `${acc} var(${tokenName(`radius-${value}`)})`, '')
      .trim(),
  );
};

export const zIndex = (index: ZIndex, stack?: StackIndex) => {
  if (stack === undefined) {
    return unsafeCSS(`var(${tokenName(`z-index-${index}`)})`);
  }

  return unsafeCSS(`var(${tokenName(`z-index-${index}`)}-${stack})`);
};

export const boxShadow = (
  x: number,
  y: number,
  blur: number,
  spread: number,
  shadowColor: CSSResult,
) =>
  unsafeCSS(`${rem(x)} ${rem(y)} ${rem(blur)} ${rem(spread)} ${shadowColor}`);
